import React, { useState, useEffect, useContext } from "react";
import styles from "./SelectOrganization.module.scss";
import { Link, useNavigate } from "react-router-dom";
import _ from "lodash";
import { useQuery, useMutation } from "@apollo/client";
import gql from "graphql-tag";
import {
  Typography,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  TextField,
  useTheme,
  Divider,
} from "@material-ui/core";
import Icon from "@mdi/react";
import { mdiLockOffOutline, mdiLockOutline, mdiTrashCanOutline } from "@mdi/js";
import { isAuthed, isVantageAdmin } from "../../utils/authorization";
import { useAuth } from "../../context/authContext";
import { SnackbarContext } from "../../context/snackbarContext";
import { DialogContext } from "../../context/dialogContext";
import { FetchContext } from "../../context/fetchContext";
import Loading from "../../components/Loading/Loading";
import { fiscalYearStart, fullDate } from "../../utils/dates";

import AddOrgDialog from "../../components/AddOrgDialog/AddOrgDialog";
import AddEditAdminDialog from "./AddEditAdminDialog";
import ConfirmDialog from "../OneYear/ConfirmDialog";
import Splash from "../../components/Splash/Splash";

const ORGANIZATIONS = "Organizations";
const ADMINS = "Admins";

const SelectOrganization = () => {
  const theme = useTheme();

  const { auth, handleSignout } = useAuth();
  const { snack } = useContext(SnackbarContext);
  const { dialog, setDialog } = useContext(DialogContext);
  const { fetch, requestFetch } = useContext(FetchContext);

  const [view, setView] = useState(ORGANIZATIONS); // organizations, admins

  const [confirmDialog, setConfirmDialog] = useState();

  const [deleteConfirm, setDeleteConfirm] = useState({ name: "", callback: null });
  const [deleteVerify, setDeleteVerify] = useState();

  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState();

  const { loading, data, refetch } = useQuery(GET_ORGANIZATIONS_AND_USERS, { variables: { ids: _.get(auth, "organizations", []) } });
  const [updateActive] = useMutation(UPDATE_ACTIVE);
  const [deleteOrg] = useMutation(DELETE_ORGANIZATION);
  const [deleteAdmin] = useMutation(DELETE_ADMIN);
  const [toggleSubscriptionBypass] = useMutation(TOGGLE_SUBSCRIPTION_BYPASS);

  const handleAddOrg = () => {
    setDialog({ ...dialog, addOrgDialog: { open: true, userId: null } });
  };

  const handleConfirmDelete = (name, callback) => {
    if (_.isNil(name)) {
      setDeleteConfirm({ name: "", callback: null });
      setDeleteVerify();
    } else {
      setDeleteConfirm({ name, callback });
    }
  };

  const handleDeleteOrg = (id) => async () => {
    try {
      const ok = await deleteOrg({ variables: { id } });

      if (ok) {
        snack(`Deleted organization`);
        requestFetch();
        setDeleteConfirm({ name: "", callback: null });
        setDeleteVerify();
      }
    } catch (err) {
      snack("Failed to delete organization", "error");
    }
  };

  const handleToggleActive = (id, active, name) => {
    setConfirmDialog({
      msg: `You are about to ${active ? "activate" : "deactivate"} ${name}. Are you sure?`,
      callback: async () => {
        try {
          const ok = await updateActive({ variables: { id, active } });

          if (ok.data.updateOrganization) {
            snack(`${active ? "Activated" : "Deactivated"} ${name}`);
            setConfirmDialog();
            requestFetch();
          }
        } catch (err) {
          snack(`Failed to ${active ? "activate" : "deactivate"} ${name}`, "error");
        }
      },
    });
  };

  const handleToggleSubscriptionBypass = async (id, name, subscriptionBypass) => {
    setConfirmDialog({
      msg: `You are about to ${
        subscriptionBypass ? "disable" : "enable"
      } the subscription bypass for the organization, ${name}. Are you sure?`,
      callback: async () => {
        try {
          const ok = await toggleSubscriptionBypass({ variables: { id } });

          if (ok) {
            snack(`Organization subscription bypass updated`);
            setConfirmDialog();
          }
        } catch (err) {
          snack("Failed to update organization subscription bypass", "error");
        }
      },
    });
  };

  const handleView = () => {
    if (view === ORGANIZATIONS) {
      setView(ADMINS);
    } else if (view === ADMINS) {
      setView(ORGANIZATIONS);
    }
  };

  const handleEditAdmin = (user) => {
    if (user) {
      setEditAdmin(_.pick(user, ["id", "name", "email", "auth", "organizations"]));
    } else {
      setEditAdmin();
    }
  };

  const handleDeleteAdmin = (id) => async () => {
    try {
      const ok = await deleteAdmin({ variables: { id } });

      if (ok) {
        snack(`Deleted admin`);
        requestFetch();
        setDeleteConfirm({ name: "", callback: null });
        setDeleteVerify();
      }
    } catch (err) {
      snack("Failed to delete admin", "error");
    }
  };

  useEffect(() => {
    refetch();
  }, [fetch]);

  const organizations = _.get(data, "organizations", []);
  const users = _.get(data, "userAdmins", []);

  return (
    <>
      <Splash isWide={true}>
        <div className={styles.contentWrapper}>
          {view === ORGANIZATIONS && (
            <>
              <Typography variant="h2" gutterBottom className={styles.title}>
                Select an Organization
              </Typography>

              {isVantageAdmin(auth) && (
                <div style={{ textAlign: "center" }}>
                  <Button
                    size="large"
                    onClick={handleAddOrg}
                    className={styles.buttonCreate}
                    style={{ fontSize: theme.typography.h6.fontSize }}
                  >
                    Create a new organization
                  </Button>
                  <Divider />
                </div>
              )}

              {loading ? (
                <Loading color="#fff" />
              ) : (
                _.sortBy(organizations, (o) => !o.active).map(({ id, name, fiscalYear, active, hasSubscriptionBypass }) => {
                  return (
                    <div
                      key={id}
                      className={active ? styles.item : styles.itemInactive}
                      style={{ borderBottom: `1px solid ${theme.palette.divider}`, padding: theme.spacing(2) }}
                    >
                      <div className={styles.itemContent} style={{ gap: theme.spacing(2) }}>
                        <Link to={`/${id}`} className={styles.link}>
                          <Typography variant="h6">{name}</Typography>
                          <Typography variant="subtitle1" className={styles.fiscalYear}>
                            <span>Fiscal Year: </span>
                            {fullDate(fiscalYearStart(fiscalYear))} - {fullDate(fiscalYear)}
                          </Typography>
                        </Link>
                        {isVantageAdmin(auth) && (
                          <div className={styles.buttonContainer} style={{ gap: theme.spacing(0.5) }}>
                            <Button
                              title={active ? "Deactivate" : "Activate"}
                              color={active ? "primary" : "secondary"}
                              variant="outlined"
                              onClick={() => handleToggleActive(id, !active, name)}
                            >
                              {active ? "Active" : "Inactive"}
                            </Button>
                            <IconButton
                              title={`${hasSubscriptionBypass ? "Disable" : "Enable"} subscription bypass`}
                              size="small"
                              onClick={() => handleToggleSubscriptionBypass(id, name, hasSubscriptionBypass)}
                            >
                              <Icon path={hasSubscriptionBypass ? mdiLockOffOutline : mdiLockOutline} size={1} color="#ef5350" />
                            </IconButton>
                            <IconButton title="Delete" size="small" onClick={() => handleConfirmDelete(name, handleDeleteOrg(id))}>
                              <Icon path={mdiTrashCanOutline} size={1} color="#ef5350" />
                            </IconButton>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}

          {isVantageAdmin(auth) && view === ADMINS && (
            <>
              <Typography variant="h2" gutterBottom className={styles.title}>
                Manage Admins
              </Typography>
              <div style={{ textAlign: "center" }}>
                <Button
                  size="large"
                  onClick={() => {
                    handleEditAdmin();
                    setAdminDialogOpen(true);
                  }}
                  className={styles.buttonCreate}
                  style={{ fontSize: theme.typography.h6.fontSize }}
                >
                  Create a new admin
                </Button>
                <Divider />
              </div>

              {_.sortBy(users, (u) => u.disabled === true).map((user) => {
                const { id, name, email, organizations, auth, disabled } = user;
                return (
                  <div
                    key={id}
                    className={!disabled ? styles.item : styles.itemInactive}
                    style={{ borderBottom: `1px solid ${theme.palette.divider}`, padding: theme.spacing(2) }}
                  >
                    <div className={styles.itemContent} style={{ gap: theme.spacing(2) }}>
                      <div>
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: `0 ${theme.spacing(1)}px` }}>
                          <Typography variant="h6">
                            {name.first} {name.last}
                          </Typography>
                          <Typography variant="body2" style={{ wordBreak: "break-all" }}>
                            {email}
                          </Typography>
                        </div>
                        <Typography variant="subtitle1" className={styles.auth}>
                          {auth}
                          {auth === "vantage facilitator" && <span> - Organization: {organizations.length}</span>}
                        </Typography>
                      </div>
                      <div className={styles.buttonContainer} style={{ gap: theme.spacing(1) }}>
                        <Button
                          color={!disabled ? "primary" : "secondary"}
                          variant="outlined"
                          onClick={() => {
                            handleEditAdmin(user);
                            setAdminDialogOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleConfirmDelete(`${name.first} ${name.last}`, handleDeleteAdmin(id))}
                          disabled={disabled === true}
                        >
                          <Icon path={mdiTrashCanOutline} size={1} color="#ef5350" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className={styles.flex} style={{ marginTop: theme.spacing(2) }}>
          {isVantageAdmin(auth) && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleView}
              className={styles.buttonCreate}
              style={{ fontSize: theme.typography.h6.fontSize }}
            >
              {view === ORGANIZATIONS ? ADMINS : ORGANIZATIONS}
            </Button>
          )}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleSignout}
            className={styles.buttonSignout}
            style={{ fontSize: theme.typography.h6.fontSize }}
          >
            Sign out
          </Button>
        </div>
      </Splash>

      <Dialog
        open={Boolean(deleteConfirm.name)}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleConfirmDelete();
          }
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span className={styles.red}>You are going to remove {deleteConfirm.name}. Are you ABSOLUTELY sure?</span>
          </DialogContentText>
          <DialogContentText>
            Please type <code>{deleteConfirm.name}</code> to proceed.
          </DialogContentText>
          <TextField variant="outlined" fullWidth value={deleteVerify || ""} onChange={(e) => setDeleteVerify(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={() => handleConfirmDelete()}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={deleteConfirm.callback} disabled={deleteVerify !== deleteConfirm.name}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!_.isEmpty(confirmDialog)}
        handleClose={() => setConfirmDialog()}
        handleConfirm={_.get(confirmDialog, "callback", () => {})}
        message={_.get(confirmDialog, "msg", "")}
      />

      <AddOrgDialog dialog={dialog} setDialog={setDialog} requestFetch={requestFetch} snack={snack} />

      <AddEditAdminDialog
        open={adminDialogOpen}
        handleClose={() => setAdminDialogOpen(false)}
        admin={editAdmin}
        organizations={organizations}
      />
    </>
  );
};

export default SelectOrganization;

const GET_ORGANIZATIONS_AND_USERS = gql`
  query ($ids: [ID!]) {
    organizations(ids: $ids) {
      id
      name
      fiscalYear
      active
      hasSubscriptionBypass
    }
    userAdmins {
      id
      name {
        first
        last
      }
      email
      organizations
      auth
      disabled
      position
      profilePicture
    }
  }
`;

const DELETE_ORGANIZATION = gql`
  mutation ($id: ID!) {
    deleteOrganization(id: $id)
  }
`;

const UPDATE_ACTIVE = gql`
  mutation ($id: ID!, $active: Boolean!) {
    updateOrganization(id: $id, active: $active)
  }
`;

const DELETE_ADMIN = gql`
  mutation ($id: ID!) {
    deleteUser(id: $id)
  }
`;

const TOGGLE_SUBSCRIPTION_BYPASS = gql`
  mutation ($id: ID!) {
    toggleSubscriptionBypass(id: $id) {
      id
      name
      fiscalYear
      active
      hasSubscriptionBypass
    }
  }
`;
